import { useMemo } from "react";
import { Button } from "../../../../components/Button";
import { ImageView } from "../../../../components/Image";
import { getInitial } from "../../../../services/utils";
import { zipCodes } from "../../../../assets/data/zip_codes";
import LocationIcon from "../../../../assets/icons/location.svg?react";

interface ProviderInfoSectionProps {
  provider: any;
  onBrowseProviders: () => void;
  onSelectLater: () => void;
  onSelectDifferentProvider?: () => void;
}

export const ProviderInfoSection: React.FC<ProviderInfoSectionProps> = ({ 
  provider, 
  onBrowseProviders, 
  onSelectLater,
  onSelectDifferentProvider
}) => {
  const isZipValid = (code: string) => code && (code.trim().length === 5 && !isNaN(parseInt(code, 10)) && !!zipCodes[code]);
  
  const [zipCity, zipState] = useMemo(() => {
    if (!provider) return [undefined, undefined];
    return isZipValid(provider.addressZip) ? [zipCodes[provider.addressZip]?.city, zipCodes[provider.addressZip]?.state] : [undefined, undefined];
  }, [provider]);
  
  const addressZip = provider?.addressZip;

  return (
    <div className="flex p-2 bg-white rounded-2xl gap-2 flex-col min-w-[230px]">
      <div className="items-start md:items-center gap-4 flex">
        {provider?.firstName ? (
          <>
            {provider?.profileImg ? (
              <ImageView
                className="w-20 h-20 rounded-2xl object-cover"
                src={provider?.profileImg || "/images/profile1.png"}
                alt="provider"
                loading="lazy"
              />
            ) : (
              <div
                className={`uppercase w-16 h-16 rounded-2xl flex justify-center items-center text-[40px] text-primary bg-secondary`}
              >
                {getInitial(provider?.firstName, provider?.lastName)}
              </div>
            )}
            <div className="flex-1 justify-between items-start md:items-center flex flex-col md:flex-row mt-[-5px]">
              <div className="flex-col justify-start items-start gap-0.5 inline-flex">
                <div className="flex-col justify-start items-start gap-0.5 flex">
                  <div className="justify-start items-start flex flex-col md:flex-row">
                    <div className="text-black text-base font-semibold mt-[3px]">
                      {provider?.firstName} {provider?.lastName}
                    </div>
                  </div>
                  {provider?.llcName && (
                    <div className="text-black text-xs font-normal ">
                      {provider?.llcName}
                    </div>
                  )}
                  <div className="text-black font-normal text-xs">
                    {provider?.profession?.join(", ")}
                  </div>
                </div>
                <div className="justify-start items-start gap-1.5 inline-flex">
                  <LocationIcon className="w-4 h-4 mt-[2px] justify-center items-center flex" />
                  <div className="text-black text-sm font-normal ">
                    {`${provider?.distanceFromZip ? `${provider?.distanceFromZip} mi.` : ''} ${zipCity ? `${zipCity}, ${zipState}` : ''} ${addressZip ? ` (${addressZip})` : `${provider?.addressState}`}`}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full">
            <Button
              variant="primary"
              className="w-full h-6 !rounded-full bg-primary mb-2"
              onClick={onBrowseProviders}
            >
              Browse & Select a Provider
            </Button>
            <Button
              variant="primary"
              className="w-full h-6 !rounded-full bg-primary"
              onClick={onSelectLater}
            >
              I'll Select a Provider Later
            </Button>
          </div>
        )}
      </div>
      
      {/* Add "Select a Different Provider" button when there is a selected provider */}
      {provider?.firstName && onSelectDifferentProvider && (
        <div className="w-full mt-3">
          <Button
            variant="outline"
            className="w-full h-8 !rounded-full border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={onSelectDifferentProvider}
          >
            Select a Different Provider
          </Button>
        </div>
      )}
    </div>
  );
};